/**
 * ContactConversationPanel (KAN-736)
 *
 * Body of a client conversation: header (name link, company, "Что делать
 * дальше", manual mode, own contact, share/copy, our account), the feed with
 * its own scroll, the account picker and the input row (paperclip,
 * "Материалы", text, "Отправить"). A flex column: the feed takes all the free
 * height. Wrapped by ContactTranscriptDialog (modal) and ContactChatPage
 * (/clients/:clientId/chat, "Широкий вид").
 *
 * History of the behaviour (moved here from ContactTranscriptDialog):
 * - TASK-208: messages from the org-scoped dashboard endpoints.
 * - KAN-626: with a clientId manual mode (only the switch and its label toggle
 *   it) and an input available only while manual mode is on.
 * - KAN-636: the feed is kept scrolled to the bottom; header shows OUR account.
 * - KAN-639: company and linked cards (TranscriptClientInfo); a linked card
 *   switches the panel to its transcript, "Назад" returns.
 * - KAN-654: with a clientId the feed is ALL messages of the client;
 *   contactId stays the estimate a manual message is recorded into.
 * - KAN-663: several of OUR accounts: a dropdown picks the account to send
 *   from (default: the latest); one account: no dropdown, no account_id.
 * - KAN-665: the name links to the client card; "Что делать дальше".
 * - KAN-686: BotSilenceStatus next to the switch.
 * - KAN-710: "Скопировать ссылку" with ?dialog=<clientId>&estimate=<id>.
 * - KAN-721/736: "Материалы" in the input row next to the paperclip.
 * - KAN-816: the next bot touch (NextBotTouchNote, from the shared client
 *   query) under the header controls, above the feed; modal and page alike.
 * - KAN-819: the lead temperature badge next to the title (tooltip: source
 *   and reason), from the same client query; nothing for a closed client.
 * - KAN-871: the manager's own personal Telegram is "От себя (@username)" in
 *   the picker; personal accounts of other managers are never offered.
 * - KAN-864: the client Telegram avatar (ClientAvatar) left of the title, from
 *   the same client query; the picture opens large on click.
 * - KAN-884: on a narrow screen (phone) with a client the panel splits into
 *   tabs "Переписка" (feed + input) and "Клиент" (the client block); the title
 *   row with the client link stays on both (ConversationPhoneTabs).
 * - KAN-929: with a clientId the manager's calls (useClientCalls) go into the
 *   feed by time as CallFeedItem blocks (mergeFeedWithCalls); a failed calls
 *   request leaves the feed as it was; only calls: no "Нет сообщений".
 * - KAN-912: with a clientId OUR deletable messages get the menu "Удалить"
 *   (useMessageDeletion: can_delete and the DELETE rule of api_access); the
 *   choice opens DeleteMessageDialog, the feed is refetched after the answer.
 * - KAN-940: the input keeps an unfinished message as a draft of the client
 *   (draftKey client:<id>, the same key as in the manager app).
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CallFeedItem } from '@/components/messages/CallFeedItem'
import { MessageBubble } from '@/components/messages/MessageBubble'
import { MessageComposer } from '@/components/messages/MessageComposer'
import { DeleteMessageDialog } from '@/components/messages/DeleteMessageDialog'
import { useMessageDeletion } from '@/hooks/useMessageDeletion'
import {
  SenderAccountLink,
  type SenderAccountLinkAccount,
} from '@/components/messages/SenderAccountLink'
import { useSenderAccountChoice } from '@/hooks/useSenderAccountChoice'
import { useClientCalls } from '@/hooks/useClientCalls'
import { mergeFeedWithCalls } from '@/lib/conversationFeed'
import { NativeSelect } from '@/components/ui/native-select'
import { Switch } from '@/components/ui/switch'
import { OwnContactSwitch } from '@/components/clients/OwnContactSwitch'
import { NextContactAction } from '@/components/clients/NextContactAction'
import { clientNextContactTarget } from '@/lib/nextContact'
import { ClientAvatar } from '@/components/clients/ClientAvatar'
import { MANUAL_CHAT_POLL_MS, useManualChat } from '@/components/clients/useManualChat'
import { BotSilenceStatus } from '@/components/clients/BotSilenceStatus'
import { NextBotTouchNote } from '@/components/clients/NextBotTouchNote'
import { ConversationLeadTemperature } from '@/components/clients/ConversationLeadTemperature'
import { useClient } from '@/services/api/clientQueries'
import { useLinkedTranscript } from '@/components/clients/useLinkedTranscript'
import {
  transcriptQueryKey,
  useTranscriptMessages,
} from '@/components/clients/useTranscriptMessages'
import { ChannelBadge } from '@/components/dashboard/ChannelBadge'
import { CopyDialogLinkButton } from '@/components/dashboard/CopyDialogLinkButton'
import { TranscriptClientInfo } from '@/components/dashboard/TranscriptClientInfo'
import { TranscriptMaterialsButton } from '@/components/dashboard/TranscriptMaterialsButton'
import { TranscriptNextSteps } from '@/components/dashboard/TranscriptNextSteps'
import { ConversationPhoneTabs } from './ConversationPhoneTabs'
import { ClientTitleLink } from './ClientTitleLink'

export interface ConversationTitleProps {
  className?: string
  children: ReactNode
}

function DefaultTitle({ className, children }: ConversationTitleProps) {
  return <h1 className={className}>{children}</h1>
}

export interface ContactConversationPanelProps {
  /**
   * Estimate of the dialog: manual messages are recorded into it; without a
   * clientId its messages form the transcript.
   */
  contactId: string | null
  /** Human-readable dialog title (client name / display name). */
  title: string
  /** Client of the dialog; without it no manual mode and no input. */
  clientId?: string | null
  /** Manual mode if the caller knows it (else read from the client). */
  manualMode?: boolean
  /**
   * Our Telegram account of the dialog. undefined: taken from the latest
   * message that has one; null: none, the header shows nothing.
   */
  senderAccount?: SenderAccountLinkAccount | null
  /** Queries run (and the feed polls) only while active (modal open). */
  active: boolean
  /** Element of the title (DialogTitle in the modal, h1 on the page). */
  TitleComponent?: ComponentType<ConversationTitleProps>
  /** Called when the user leaves via the client link (the modal closes). */
  onLeave?: () => void
  /** Extra control next to the title for the shown client ("Широкий вид"). */
  titleAction?: (clientId: string) => ReactNode
  className?: string
  /** Classes of the header block (the modal leaves room for its close cross). */
  headerClassName?: string
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ContactConversationPanel({
  contactId: callerContactId,
  title: callerTitle,
  clientId: callerClientId = null,
  manualMode: callerManualMode,
  senderAccount: callerSenderAccount,
  active,
  TitleComponent = DefaultTitle,
  onLeave,
  titleAction,
  className,
  headerClassName,
}: ContactConversationPanelProps) {
  const linked = useLinkedTranscript(active, callerContactId)
  const target = linked.target
  const contactId = target ? target.estimateId : callerContactId
  const title = target ? target.title : callerTitle
  const clientId = target ? target.clientId : callerClientId
  const initialManualMode = target ? undefined : callerManualMode
  const senderAccount = target ? undefined : callerSenderAccount
  const manualSwitchId = useId()
  // The key of the visible feed also goes to useManualChat: a manual send and
  // the manual mode switch refresh exactly this feed.
  const transcriptKey = useMemo(
    () => transcriptQueryKey(clientId, contactId),
    [clientId, contactId]
  )
  // KAN-663: the feed is read before the chat hook because the account to send
  // from (and its estimate) comes from the feed; polling follows manual mode
  // through a mirrored state, one render behind the switch.
  const [pollFeed, setPollFeed] = useState(false)
  const {
    data: messages,
    isLoading,
    error,
  } = useTranscriptMessages({
    clientId,
    estimateId: contactId,
    enabled: active,
    poll: !!clientId && pollFeed,
  })
  // KAN-929: manager calls of the client between the messages (the cabinet
  // feed has no "load earlier", so every call is shown); refreshed with the
  // feed: same polling, and the key lives under the feed key.
  const calls = useClientCalls(clientId, {
    enabled: active,
    refetchInterval: active && !!clientId && pollFeed ? MANUAL_CHAT_POLL_MS : false,
  })
  const feedItems = useMemo(
    () => mergeFeedWithCalls(messages ?? [], calls, { hasMore: false }),
    [messages, calls]
  )
  // The dropdown exists only with a choice; then the request always names the
  // account (the chosen one or the latest), so the backend does not guess.
  // KAN-871: own personal account "От себя", other personal ones are hidden.
  // KAN-686: why the bot is silent (shared ['client', id] query, fetched once).
  // KAN-871: its account is the company option next to a personal one.
  const { data: clientCard } = useClient(clientId, active)
  const {
    options: accountOptions,
    sendOption,
    sendChannel,
    chooseAccount: setChosenAccountId,
    account: hookAccount,
  } = useSenderAccountChoice(messages, {
    enabled: !!clientId,
    resetKey: clientId,
    fallbackAccount: clientCard?.sender_account ?? null,
  })
  const chat = useManualChat({
    clientId,
    initialManualMode,
    transcriptKey,
    enabled: active,
    estimateId: sendOption ? (sendOption.estimateId ?? contactId) : contactId,
    accountId: sendOption?.accountId ?? null,
    channel: sendChannel,
  })
  useEffect(() => setPollFeed(chat.manualMode), [chat.manualMode])
  const deletion = useMessageDeletion(clientId)
  // Shown "our account" = the one the message goes from (KAN-871 M1); the
  // caller's account only names it when nothing is chosen explicitly.
  const account = sendOption
    ? sendOption.account
    : senderAccount === undefined
      ? hookAccount
      : senderAccount

  // Keep the feed scrolled to the latest message on open, on new messages and
  // when the phone tab "Переписка" is shown again (KAN-884).
  const feedRef = useRef<HTMLDivElement>(null)
  const scrollFeedToBottom = useCallback(() => {
    const feed = feedRef.current
    if (feed) feed.scrollTop = feed.scrollHeight
  }, [])
  const lastItemId = feedItems[feedItems.length - 1]?.id
  useEffect(() => {
    if (active) scrollFeedToBottom()
  }, [active, isLoading, lastItemId, feedItems.length, scrollFeedToBottom])

  const titleRow = (
    <>
      {target && (
        <button
          type="button"
          className="inline-flex w-fit items-center gap-1 text-xs text-gray-500 hover:text-gray-900"
          onClick={linked.back}
          data-testid="transcript-linked-back"
        >
          <ArrowLeft className="h-3 w-3" />
          Назад: {callerTitle}
        </button>
      )}
      <div className="flex min-w-0 items-start gap-2">
        {/* KAN-864: the client Telegram avatar; the picture opens large on click. */}
        {clientId && clientCard && <ClientAvatar avatarUrl={clientCard.avatar_url} name={clientCard.name ?? ''} size={40} />}
        <TitleComponent className="min-w-0 flex-1 text-lg font-semibold leading-tight tracking-tight">
          {clientId ? (
            <ClientTitleLink clientId={clientId} title={title} onClick={onLeave} />
          ) : (
            title
          )}
        </TitleComponent>
        {clientId && (
          <ConversationLeadTemperature client={clientCard} className="mt-0.5" />
        )}
        {clientId && titleAction?.(clientId)}
      </div>
    </>
  )

  const clientBlock = (
    <>
      {clientId && (
        <TranscriptClientInfo
          clientId={clientId}
          enabled={active}
          onOpenClient={(client) => void linked.openLinked(client)}
          openingClientId={linked.loadingClientId}
        />
      )}
      {clientId && <TranscriptNextSteps clientId={clientId} enabled={active} />}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
        {clientId && (
          <div className="inline-flex w-fit flex-wrap items-center gap-2 text-sm">
            <Switch
              id={manualSwitchId}
              checked={chat.manualMode}
              onCheckedChange={chat.setManualMode}
              disabled={chat.isToggling}
              data-testid="manual-mode-switch"
            />
            <label
              htmlFor={manualSwitchId}
              className="cursor-pointer font-medium text-gray-900"
            >
              Ручное управление
            </label>
            <BotSilenceStatus
              clientId={clientId}
              manualMode={chat.manualMode}
              botSilence={clientCard?.bot_silence}
              testId="manual-mode-hint"
            />
          </div>
        )}
        {/* KAN-723: own (test) contact switch; value from the shared client query. */}
        {clientId && clientCard && (
          <OwnContactSwitch clientId={clientId} isOwn={!!clientCard.is_own} />
        )}
        {/* KAN-710: a link to this dialog to share. */}
        {(clientId || contactId) && (
          <CopyDialogLinkButton clientId={clientId} estimateId={contactId} />
        )}
        {/* KAN-960: следующий контакт (звонок или встреча), клиент берётся в работу. */}
        {clientId && clientCard && <NextContactAction target={clientNextContactTarget(clientCard)} />}
        {/* KAN-648: аватар и метка аккаунта, ссылка в Настройки > Telegram */}
        {account && (
          <SenderAccountLink
            account={account}
            testId="transcript-sender-account"
            className="sm:ml-auto"
          />
        )}
      </div>
      {/* KAN-816: when and what the bot writes next, with its actions. */}
      {clientId && clientCard?.next_bot_touch && (
        <div className="pt-1">
          <NextBotTouchNote clientId={clientId} touch={clientCard.next_bot_touch} />
        </div>
      )}
    </>
  )

  const chatBody = (
    <>
      <div
        ref={feedRef}
        className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1"
        data-testid="transcript-feed"
      >
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Загрузка переписки…
          </div>
        )}

        {error && !isLoading && (
          <p className="py-8 text-center text-sm text-red-600">
            Не удалось загрузить переписку
          </p>
        )}

        {!isLoading && !error && feedItems.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-400">Нет сообщений</p>
        )}

        {!isLoading &&
          !error &&
          feedItems.map((item) =>
            item.kind === 'call' ? (
              <CallFeedItem
                key={`call-${item.id}`}
                call={item.call}
                time={formatTime(item.at)}
              />
            ) : (
              <MessageBubble
                key={item.id}
                message={item.message}
                channel={<ChannelBadge source={item.message.source} />}
                time={formatTime(item.message.created_at)}
                onDelete={deletion.handlerFor(item.message)}
              />
            )
          )}
      </div>

      {clientId && accountOptions.length > 1 && sendOption && (
        <label
          className="flex shrink-0 items-center gap-2 text-xs text-gray-600"
          data-testid="sender-account-picker"
        >
          <span className="shrink-0">С аккаунта:</span>
          <NativeSelect
            className="h-8 w-auto min-w-[10rem]"
            value={sendOption.key}
            onChange={(event) => setChosenAccountId(event.target.value)}
            options={accountOptions.map((option) => ({
              value: option.key,
              label: option.label,
            }))}
            aria-label="С аккаунта"
            data-testid="sender-account-select"
          />
        </label>
      )}
      {clientId && (
        <MessageComposer
          onSend={chat.send}
          isSending={chat.isSending}
          disabled={!chat.manualMode}
          draftKey={`client:${clientId}`}
          hint={
            chat.manualMode
              ? undefined
              : 'Включите ручное управление, чтобы написать клиенту'
          }
          leading={
            // KAN-721/736: send a material of the operator (price list, catalog, photo).
            <TranscriptMaterialsButton clientId={clientId} />
          }
        />
      )}
    </>
  )

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col gap-3', className)}>
      <ConversationPhoneTabs
        titleRow={titleRow}
        clientBlock={clientBlock}
        withTabs={!!clientId}
        chat={chatBody}
        headerClassName={headerClassName}
        onChatShow={scrollFeedToBottom}
      />
      {clientId && (
        <DeleteMessageDialog
          clientId={clientId}
          message={deletion.target}
          onClose={deletion.close}
        />
      )}
    </div>
  )
}
